export type LoginVisualState = "idle" | "loading" | "error" | "success" | "totp_pending";
export type MfaMethod = "push" | "totp" | "webauthn";

export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
}

export interface MfaPendingResponse {
  readonly status: "mfa_pending";
  readonly mfaTransactionRef: string;
  readonly availableMethods: readonly MfaMethod[];
  readonly expiresAt: string;
  readonly correlationId: string;
}

export interface OperatorTokenResponse {
  readonly token: string;
  readonly tokenType: "Bearer";
  readonly expiresIn: number;
}

export interface OperatorAuthState {
  readonly visualState: LoginVisualState;
  readonly message: string;
  readonly mfaTransactionRef: string | undefined;
}

export interface CredentialProvider {
  isAvailable(): boolean;
  getAssertion(
    publicKeyRequestOptions: Readonly<Record<string, unknown>>,
    signal: AbortSignal,
  ): Promise<Readonly<Record<string, unknown>>>;
}

export interface OperatorAuthControllerOptions {
  readonly apiBaseUrl?: string | URL;
  readonly credentialProvider: CredentialProvider;
  readonly fetchImplementation: typeof fetch;
  readonly onAuthenticated: (response: OperatorTokenResponse) => void;
  readonly now?: () => number;
}

interface ActiveAttempt {
  readonly id: number;
  readonly abortController: AbortController;
  expiresAt: number | undefined;
  reference: string | undefined;
}

interface PushContinueResponse {
  readonly status: string;
  readonly method: "push";
  readonly pollUrl: string;
  readonly pollAfterSeconds: number;
  readonly numberMatchingCode: number | undefined;
  readonly expiresAt: string;
}

interface WebAuthnContinueResponse {
  readonly status: string;
  readonly method: "webauthn";
  readonly publicKeyRequestOptions: Readonly<Record<string, unknown>>;
  readonly expiresAt: string;
}

interface TotpContinueResponse {
  readonly status: string;
  readonly method: "totp";
  readonly totpCodeLength: 6;
  readonly expiresAt: string;
}

interface MfaStatusResponse {
  readonly status: string;
  readonly expiresAt: string;
  readonly numberMatchingCode: number | undefined;
  readonly pollAfterSeconds: number | undefined;
  readonly tokenUrl: string | undefined;
}

class StaleAttemptError extends Error {}

class AuthResponseError extends Error {
  readonly response: Response;

  constructor(response: Response) {
    super(`Authentication request failed with status ${response.status}`);
    this.response = response;
  }
}

class FetchNetworkError extends Error {
  constructor(_cause: unknown) {
    super("Authentication request could not reach the server");
  }
}

const idleState: OperatorAuthState = {
  visualState: "idle",
  message: "",
  mfaTransactionRef: undefined,
};

export class OperatorAuthController {
  readonly #apiBaseUrl: URL | undefined;
  readonly #credentials: CredentialProvider;
  readonly #fetch: typeof fetch;
  readonly #onAuthenticated: (response: OperatorTokenResponse) => void;
  readonly #now: () => number;
  readonly #listeners = new Set<() => void>();
  #attemptSequence = 0;
  #activeAttempt: ActiveAttempt | undefined;
  #activeFlow: Promise<void> | undefined;
  #state: OperatorAuthState = idleState;
  #totpResolver: ((code: string) => void) | undefined = undefined;

  constructor(options: OperatorAuthControllerOptions) {
    this.#apiBaseUrl = options.apiBaseUrl === undefined
      ? undefined
      : new URL(
        options.apiBaseUrl.toString().endsWith("/")
          ? options.apiBaseUrl.toString()
          : `${options.apiBaseUrl.toString()}/`,
      );
    this.#credentials = options.credentialProvider;
    this.#fetch = options.fetchImplementation;
    this.#onAuthenticated = options.onAuthenticated;
    this.#now = options.now ?? Date.now;
  }

  getSnapshot = (): OperatorAuthState => this.#state;

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  submit(credentials: LoginCredentials): Promise<void> {
    if (this.#activeFlow !== undefined) {
      return this.#activeFlow;
    }
    const attempt: ActiveAttempt = {
      id: ++this.#attemptSequence,
      abortController: new AbortController(),
      expiresAt: undefined,
      reference: undefined,
    };
    this.#activeAttempt = attempt;
    this.#setState({
      visualState: "loading",
      message: "Verificando suas credenciais…",
      mfaTransactionRef: undefined,
    });
    const flow = this.#run(credentials, attempt).finally(() => {
      if (this.#activeFlow === flow) {
        this.#activeFlow = undefined;
      }
      if (this.#activeAttempt?.id === attempt.id) {
        this.#activeAttempt = undefined;
      }
    });
    this.#activeFlow = flow;
    return flow;
  }

  submitTotp(code: string): void {
    if (this.#totpResolver === undefined) return;
    const resolve = this.#totpResolver;
    this.#totpResolver = undefined;
    resolve(code);
  }

  async cancel(): Promise<void> {
    this.#totpResolver = undefined;
    const attempt = this.#abandonActiveAttempt();
    this.#setState(idleState);
    if (attempt?.reference !== undefined) {
      await this.#cancelRemote(attempt.reference);
    }
  }

  destroy(): void {
    this.#abandonActiveAttempt();
    this.#setState(idleState);
    this.#listeners.clear();
  }

  async #run(credentials: LoginCredentials, attempt: ActiveAttempt): Promise<void> {
    try {
      const pending = parsePendingResponse(await this.#requestJson(
        attempt,
        "/api/v1/auth/login",
        jsonRequest("POST", credentials),
      ));
      attempt.reference = pending.mfaTransactionRef;
      attempt.expiresAt = parseExpiry(pending.expiresAt);
      this.#assertActive(attempt);
      this.#setState({
        visualState: "loading",
        message: "Confirme o segundo fator para continuar…",
        mfaTransactionRef: pending.mfaTransactionRef,
      });

      const method = selectMethod(pending.availableMethods, this.#credentials.isAvailable());
      const continuePayload = await this.#requestJson(
        attempt,
        `/api/v1/auth/mfa/transactions/${encodeURIComponent(pending.mfaTransactionRef)}/continue`,
        jsonRequest("POST", { method }),
      );

      let tokenUrl: string;
      if (method === "webauthn") {
        tokenUrl = await this.#completeWebAuthn(attempt, parseWebAuthnContinue(continuePayload));
      } else if (method === "totp") {
        tokenUrl = await this.#completeTotp(attempt, parseTotpContinue(continuePayload));
      } else {
        tokenUrl = await this.#completePush(attempt, parsePushContinue(continuePayload));
      }
      const token = parseTokenResponse(await this.#requestJson(
        attempt,
        tokenUrl,
        jsonRequest("POST", {}),
      ));
      this.#assertActive(attempt);
      attempt.reference = undefined;
      this.#onAuthenticated(token);
      this.#setState({
        visualState: "success",
        message: "Acesso confirmado.",
        mfaTransactionRef: undefined,
      });
    } catch (error: unknown) {
      if (error instanceof StaleAttemptError || !this.#isActive(attempt)) {
        return;
      }
      const reference = attempt.reference;
      this.#abandonActiveAttempt();
      if (reference !== undefined) {
        await this.#cancelRemote(reference);
      }
      this.#setState({
        visualState: "error",
        message: isColdStartError(error)
          ? "O piloto gratuito está iniciando. Aguarde alguns segundos e tente novamente."
          : "Não foi possível concluir o acesso. Tente novamente.",
        mfaTransactionRef: undefined,
      });
    }
  }

  #awaitTotp(attempt: ActiveAttempt, codeLength: number): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.#totpResolver = resolve;
      this.#setState({
        visualState: "totp_pending",
        message: `Digite o código de ${codeLength} dígitos do aplicativo autenticador.`,
        mfaTransactionRef: attempt.reference,
      });
      attempt.abortController.signal.addEventListener("abort", () => {
        this.#totpResolver = undefined;
        reject(new DOMException("Aborted", "AbortError"));
      }, { once: true });
    });
  }

  async #completeWebAuthn(
    attempt: ActiveAttempt,
    continued: WebAuthnContinueResponse,
  ): Promise<string> {
    attempt.expiresAt = parseExpiry(continued.expiresAt);
    this.#assertActive(attempt);
    const assertion = await this.#credentials.getAssertion(
      continued.publicKeyRequestOptions,
      attempt.abortController.signal,
    );
    this.#assertActive(attempt);
    const verified = asObject(await this.#requestJson(
      attempt,
      `/api/v1/auth/mfa/transactions/${encodeURIComponent(requiredReference(attempt))}/verify`,
      jsonRequest("POST", { method: "webauthn", assertion }),
    ));
    if (verified.status !== "mfa_succeeded" || typeof verified.tokenUrl !== "string") {
      throw new TypeError("Invalid MFA verification response");
    }
    return verified.tokenUrl;
  }

  async #completeTotp(
    attempt: ActiveAttempt,
    continued: TotpContinueResponse,
  ): Promise<string> {
    attempt.expiresAt = parseExpiry(continued.expiresAt);
    this.#assertActive(attempt);
    const code = await this.#awaitTotp(attempt, continued.totpCodeLength);
    this.#assertActive(attempt);
    const verified = asObject(await this.#requestJson(
      attempt,
      `/api/v1/auth/mfa/transactions/${encodeURIComponent(requiredReference(attempt))}/verify`,
      jsonRequest("POST", { method: "totp", code }),
    ));
    if (verified.status !== "mfa_succeeded" || typeof verified.tokenUrl !== "string") {
      throw new TypeError("Invalid MFA verification response");
    }
    return verified.tokenUrl;
  }

  async #completePush(
    attempt: ActiveAttempt,
    continued: PushContinueResponse,
  ): Promise<string> {
    attempt.expiresAt = parseExpiry(continued.expiresAt);
    this.#updatePushMessage(attempt, continued.numberMatchingCode);
    let intervalSeconds = continued.pollAfterSeconds;
    while (true) {
      await delay(intervalSeconds * 1_000, attempt.abortController.signal);
      this.#assertActive(attempt);
      let response: Response;
      try {
        response = await this.#request(attempt, continued.pollUrl, { method: "GET" });
      } catch (error: unknown) {
        if (error instanceof AuthResponseError && error.response.status === 429) {
          intervalSeconds = Math.max(
            intervalSeconds,
            retryAfterSeconds(error.response.headers.get("Retry-After"), this.#now()),
          );
          continue;
        }
        throw error;
      }
      const responseRetryAfter = retryAfterSeconds(
        response.headers.get("Retry-After"),
        this.#now(),
      );
      const status = parseStatusResponse(await response.json());
      attempt.expiresAt = parseExpiry(status.expiresAt);
      if (status.status === "mfa_pending") {
        this.#updatePushMessage(attempt, status.numberMatchingCode);
      }
      if (status.status === "mfa_succeeded" && status.tokenUrl !== undefined) {
        return status.tokenUrl;
      }
      if (status.status !== "mfa_pending") {
        throw new TypeError("MFA transaction is terminal");
      }
      intervalSeconds = Math.max(
        responseRetryAfter,
        status.pollAfterSeconds ?? intervalSeconds,
      );
    }
  }

  async #requestJson(
    attempt: ActiveAttempt,
    input: RequestInfo | URL,
    init: RequestInit,
  ): Promise<unknown> {
    const response = await this.#request(attempt, input, init);
    return await response.json() as unknown;
  }

  async #request(
    attempt: ActiveAttempt,
    input: RequestInfo | URL,
    init: RequestInit,
  ): Promise<Response> {
    this.#assertActive(attempt);
    let response: Response;
    try {
      response = await this.#fetch(this.#resolveRequestInput(input), {
        ...init,
        cache: "no-store",
        credentials: "include",
        signal: attempt.abortController.signal,
      });
    } catch (error: unknown) {
      throw new FetchNetworkError(error);
    }
    this.#assertActive(attempt);
    if (!response.ok) {
      throw new AuthResponseError(response);
    }
    return response;
  }

  async #cancelRemote(reference: string): Promise<void> {
    try {
      await this.#fetch(
        this.#resolveRequestInput(
          `/api/v1/auth/mfa/transactions/${encodeURIComponent(reference)}/cancel`,
        ),
        {
          ...jsonRequest("POST", {}),
          cache: "no-store",
          credentials: "include",
          keepalive: true,
        },
      );
    } catch {
      // Local cancellation is authoritative for the browser attempt.
    }
  }

  #resolveRequestInput(input: RequestInfo | URL): RequestInfo | URL {
    if (this.#apiBaseUrl === undefined || input instanceof Request) {
      return input;
    }
    const serialized = input instanceof URL ? input.toString() : input;
    if (typeof serialized !== "string") {
      return input;
    }
    if (/^https?:\/\//u.test(serialized)) {
      return input;
    }
    const relativePath = serialized.replace(/^\/api\/v1\/?/u, "");
    return new URL(relativePath, this.#apiBaseUrl);
  }

  #assertActive(attempt: ActiveAttempt): void {
    if (!this.#isActive(attempt)) {
      throw new StaleAttemptError();
    }
    if (attempt.expiresAt !== undefined && attempt.expiresAt <= this.#now()) {
      throw new TypeError("MFA transaction expired");
    }
  }

  #isActive(attempt: ActiveAttempt): boolean {
    return this.#activeAttempt?.id === attempt.id && !attempt.abortController.signal.aborted;
  }

  #abandonActiveAttempt(): ActiveAttempt | undefined {
    const attempt = this.#activeAttempt;
    this.#activeAttempt = undefined;
    attempt?.abortController.abort();
    return attempt;
  }

  #setState(state: OperatorAuthState): void {
    this.#state = state;
    for (const listener of this.#listeners) {
      listener();
    }
  }

  #updatePushMessage(attempt: ActiveAttempt, numberMatchingCode: number | undefined): void {
    if (numberMatchingCode === undefined) {
      return;
    }
    this.#setState({
      visualState: "loading",
      message: `Confirme o número ${String(numberMatchingCode).padStart(2, "0")} no aplicativo autenticador.`,
      mfaTransactionRef: requiredReference(attempt),
    });
  }
}

function isColdStartError(error: unknown): boolean {
  return error instanceof FetchNetworkError || (
    error instanceof AuthResponseError && [502, 503, 504].includes(error.response.status)
  );
}

function selectMethod(methods: readonly MfaMethod[], webAuthnAvailable: boolean): MfaMethod {
  if (webAuthnAvailable && methods.includes("webauthn")) {
    return "webauthn";
  }
  if (methods.includes("push")) {
    return "push";
  }
  if (methods.includes("totp")) {
    return "totp";
  }
  throw new TypeError("No supported MFA method is available");
}

function jsonRequest(method: "POST", body: unknown): RequestInit {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

function parsePendingResponse(value: unknown): MfaPendingResponse {
  const object = asObject(value);
  if (
    object.status !== "mfa_pending" ||
    typeof object.mfaTransactionRef !== "string" ||
    !Array.isArray(object.availableMethods) ||
    !object.availableMethods.every(
      (method) => method === "push" || method === "totp" || method === "webauthn",
    ) ||
    typeof object.expiresAt !== "string" ||
    typeof object.correlationId !== "string"
  ) {
    throw new TypeError("Invalid MFA pending response");
  }
  return {
    status: "mfa_pending",
    mfaTransactionRef: object.mfaTransactionRef,
    availableMethods: object.availableMethods,
    expiresAt: object.expiresAt,
    correlationId: object.correlationId,
  };
}

function parsePushContinue(value: unknown): PushContinueResponse {
  const object = asObject(value);
  if (
    object.method !== "push" ||
    typeof object.status !== "string" ||
    typeof object.pollUrl !== "string" ||
    typeof object.pollAfterSeconds !== "number" ||
    typeof object.expiresAt !== "string"
  ) {
    throw new TypeError("Invalid push continuation response");
  }
  return {
    status: object.status,
    method: "push",
    pollUrl: object.pollUrl,
    pollAfterSeconds: parsePollAfterSeconds(object.pollAfterSeconds, false),
    numberMatchingCode: parseNumberMatchingCode(object.numberMatchingCode),
    expiresAt: object.expiresAt,
  };
}

function parseWebAuthnContinue(value: unknown): WebAuthnContinueResponse {
  const object = asObject(value);
  if (
    object.method !== "webauthn" ||
    typeof object.status !== "string" ||
    typeof object.publicKeyRequestOptions !== "object" ||
    object.publicKeyRequestOptions === null ||
    Array.isArray(object.publicKeyRequestOptions) ||
    typeof object.expiresAt !== "string"
  ) {
    throw new TypeError("Invalid WebAuthn continuation response");
  }
  const publicKeyRequestOptions = asObject(object.publicKeyRequestOptions);
  if (
    publicKeyRequestOptions["mode"] === "totp" ||
    publicKeyRequestOptions["mode"] === "no_mfa"
  ) {
    throw new TypeError("Invalid WebAuthn continuation response");
  }
  return {
    status: object.status,
    method: "webauthn",
    publicKeyRequestOptions,
    expiresAt: object.expiresAt,
  };
}

function parseTotpContinue(value: unknown): TotpContinueResponse {
  const object = asObject(value);
  if (
    object.method !== "totp" ||
    typeof object.status !== "string" ||
    object.totpCodeLength !== 6 ||
    typeof object.expiresAt !== "string"
  ) {
    throw new TypeError("Invalid TOTP continuation response");
  }
  return {
    status: object.status,
    method: "totp",
    totpCodeLength: 6,
    expiresAt: object.expiresAt,
  };
}

function parseStatusResponse(value: unknown): MfaStatusResponse {
  const object = asObject(value);
  if (typeof object.status !== "string" || typeof object.expiresAt !== "string") {
    throw new TypeError("Invalid MFA status response");
  }
  return {
    status: object.status,
    expiresAt: object.expiresAt,
    numberMatchingCode: parseNumberMatchingCode(object.numberMatchingCode),
    pollAfterSeconds: parsePollAfterSeconds(object.pollAfterSeconds, true),
    tokenUrl: typeof object.tokenUrl === "string" ? object.tokenUrl : undefined,
  };
}

function parseNumberMatchingCode(value: unknown): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 99) {
    return value;
  }
  throw new TypeError("Invalid MFA number matching code");
}

function parsePollAfterSeconds(value: unknown, optional: false): number;
function parsePollAfterSeconds(value: unknown, optional: true): number | undefined;
function parsePollAfterSeconds(value: unknown, optional: boolean): number | undefined {
  if (value === undefined && optional) {
    return undefined;
  }
  if (typeof value === "number" && Number.isInteger(value) && value >= 2) {
    return value;
  }
  throw new TypeError("Invalid MFA polling interval");
}

function parseTokenResponse(value: unknown): OperatorTokenResponse {
  const object = asObject(value);
  if (
    typeof object.token !== "string" ||
    object.tokenType !== "Bearer" ||
    typeof object.expiresIn !== "number"
  ) {
    throw new TypeError("Invalid operator token response");
  }
  return { token: object.token, tokenType: "Bearer", expiresIn: object.expiresIn };
}

function asObject(value: unknown): Readonly<Record<string, unknown>> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError("Expected a JSON object");
  }
  return value as Readonly<Record<string, unknown>>;
}

function requiredReference(attempt: ActiveAttempt): string {
  if (attempt.reference === undefined) {
    throw new TypeError("Missing MFA transaction reference");
  }
  return attempt.reference;
}

function parseExpiry(value: string): number {
  const expiry = Date.parse(value);
  if (!Number.isFinite(expiry)) {
    throw new TypeError("Invalid MFA expiry");
  }
  return expiry;
}

function retryAfterSeconds(value: string | null, now: number): number {
  if (value === null) {
    return 0;
  }
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds;
  }
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, Math.ceil((date - now) / 1_000)) : 0;
}

function delay(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener("abort", () => {
      window.clearTimeout(timeout);
      reject(new DOMException("The operation was aborted", "AbortError"));
    }, { once: true });
  });
}

export function createBrowserCredentialProvider(
  credentials: Pick<CredentialsContainer, "get"> = navigator.credentials,
): CredentialProvider {
  return {
    isAvailable: () =>
      typeof window !== "undefined" && "PublicKeyCredential" in window,
    getAssertion: async (options, signal) => {
      const credential = await credentials.get({
        publicKey: parsePublicKeyRequestOptions(options),
        signal,
      });
      if (credential === null) {
        throw new DOMException("WebAuthn was canceled", "NotAllowedError");
      }
      return serializeAssertion(credential);
    },
  };
}

interface AssertionCredential extends Credential {
  readonly rawId: ArrayBuffer;
  readonly response: {
    readonly authenticatorData: ArrayBuffer;
    readonly clientDataJSON: ArrayBuffer;
    readonly signature: ArrayBuffer;
    readonly userHandle: ArrayBuffer | null;
  };
  getClientExtensionResults(): AuthenticationExtensionsClientOutputs;
}

function parsePublicKeyRequestOptions(
  options: Readonly<Record<string, unknown>>,
): PublicKeyCredentialRequestOptions {
  if (typeof options.challenge !== "string") {
    throw new TypeError("Invalid WebAuthn challenge");
  }
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: decodeBase64Url(options.challenge),
  };
  if (typeof options.timeout === "number") {
    publicKey.timeout = options.timeout;
  }
  if (typeof options.rpId === "string") {
    publicKey.rpId = options.rpId;
  }
  if (
    options.userVerification === "discouraged" ||
    options.userVerification === "preferred" ||
    options.userVerification === "required"
  ) {
    publicKey.userVerification = options.userVerification;
  }
  if (Array.isArray(options.allowCredentials)) {
    publicKey.allowCredentials = options.allowCredentials.map((value) => {
      const descriptor = asObject(value);
      if (typeof descriptor.id !== "string" || descriptor.type !== "public-key") {
        throw new TypeError("Invalid WebAuthn credential descriptor");
      }
      const result: PublicKeyCredentialDescriptor = {
        id: decodeBase64Url(descriptor.id),
        type: "public-key",
      };
      if (
        Array.isArray(descriptor.transports) &&
        descriptor.transports.every((transport) => typeof transport === "string")
      ) {
        result.transports = descriptor.transports as AuthenticatorTransport[];
      }
      return result;
    });
  }
  return publicKey;
}

function serializeAssertion(credential: Credential): Readonly<Record<string, unknown>> {
  const assertion = credential as AssertionCredential;
  if (
    !(assertion.rawId instanceof ArrayBuffer) ||
    !(assertion.response?.authenticatorData instanceof ArrayBuffer) ||
    !(assertion.response.clientDataJSON instanceof ArrayBuffer) ||
    !(assertion.response.signature instanceof ArrayBuffer)
  ) {
    throw new TypeError("Invalid WebAuthn assertion");
  }
  return {
    id: assertion.id,
    rawId: encodeBase64Url(assertion.rawId),
    type: assertion.type,
    clientExtensionResults: assertion.getClientExtensionResults(),
    response: {
      authenticatorData: encodeBase64Url(assertion.response.authenticatorData),
      clientDataJSON: encodeBase64Url(assertion.response.clientDataJSON),
      signature: encodeBase64Url(assertion.response.signature),
      userHandle: assertion.response.userHandle === null
        ? null
        : encodeBase64Url(assertion.response.userHandle),
    },
  };
}

function decodeBase64Url(value: string): ArrayBuffer {
  const normalized = value.replace(/-/gu, "+").replace(/_/gu, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const decoded = atob(padded);
  const bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  return bytes.buffer;
}

function encodeBase64Url(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "");
}
